import { classNamesFunction, getTheme, Stack } from '@fluentui/react';
import { FluentProvider, Spinner, webLightTheme } from '@fluentui/react-components';
import React, { useEffect } from 'react';
import { EmptyLayout } from '../../../layout/EmptyLayout/EmptyLayout';
import { injectIntlWithObserver } from '../../../utils';
import { onChangeReportId } from '../@data/actions';
import { CustomCalender } from '../components/CustomCalender/CustomCalender';
import { ICustomCalenderDateRange, RangeType } from '../components/CustomCalender/CustomCalender.types';
import { OverviewDataDelayMessageBar } from '../components/DataDelayMessageBar/DataDelayMessageBar';
import { ReportCardType } from '../DashboardCards/ReportCard/ReportCard.types';
import { usePubCenterReportTypes, usePublisherProfileDetails } from '../hooks/useReportQuery';
import { getInitialDateRange } from '../utils/getInitialDateRange';
import { PubcenterOverviewReportCards } from './components/PubcenterOverviewReportCards';
import OverviewPageMessages from './OverviewPage.messages';
import { getStyles } from './OverViewPage.styles';
import { IOverviewPageProps, IOverviewPageStyleProps, IOverviewPageStyles } from './OverViewPage.types';
import { DEFAULT_SHOW_FLAG } from './utils/constants';

const getClassNames = classNamesFunction<IOverviewPageStyleProps, IOverviewPageStyles>();

const SYNDICATION_REPORTS: ReportCardType[] = [
  ReportCardType.ACCOUNT,
  ReportCardType.SITE,
  ReportCardType.ADUNIT,
  ReportCardType.TYPE_TAG,
  ReportCardType.PTQs,
];
const DISTRIBUTION_REPORTS: ReportCardType[] = [
  ReportCardType.distributionV2,
  ReportCardType.DistributionPartnerTagV2,
  ReportCardType.DistributionPTQS,
  ReportCardType.DistributionPTQSSnapshot,
];

export const OverviewPage = injectIntlWithObserver<IOverviewPageProps>((props: IOverviewPageProps) => {
  const classNames = getClassNames(getStyles, { theme: getTheme() });

  const {
    intl: { formatMessage },
  } = props;

  const {
    dateRange: { initialStartDate, initialEndDate },
    initialDateRangeType,
  } = getInitialDateRange();

  const [dateRange, setDateRange] = React.useState<ICustomCalenderDateRange>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const { data: publisher, isLoading: isPublisherLoading } = usePublisherProfileDetails();
  const { data: reportTypesData, isLoading: isReportTypesLoading } = usePubCenterReportTypes();

  const syndicationReports = SYNDICATION_REPORTS.filter((report) =>
    reportTypesData?.some(
      (r) => r.id.toLowerCase() === report.toLowerCase() && DEFAULT_SHOW_FLAG[report] && DEFAULT_SHOW_FLAG[report] === 'Syndication'
    )
  );

  const distributionReports = DISTRIBUTION_REPORTS.filter((report) =>
    reportTypesData?.some(
      (r) =>
        r.id.toLowerCase() === report.toLowerCase() &&
        DEFAULT_SHOW_FLAG[report] &&
        ((DEFAULT_SHOW_FLAG[report] === 'DistributionV2' && publisher?.allowDistributionV2Reporting) ||
          (DEFAULT_SHOW_FLAG[report] === 'Distribution' && publisher?.allowDistributionReporting))
    )
  );

  const overviewReports = publisher?.allowSyndicationReporting ? syndicationReports : distributionReports;

  const onSelectedDatesChanged = (selectedDateRange: ICustomCalenderDateRange, rangeType: RangeType) => {
    setDateRange({ ...selectedDateRange });
  };

  useEffect(() => {
    if (
      publisher &&
      !publisher.allowSyndicationReporting &&
      !publisher.allowDistributionReporting &&
      !publisher.allowDistributionV2Reporting
    ) {
      onChangeReportId(ReportCardType.StartDistributionContent, props.history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisher]);

  useEffect(() => {
    onSelectedDatesChanged(dateRange, initialDateRangeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <EmptyLayout>
      <FluentProvider theme={webLightTheme}>
        {publisher && !isPublisherLoading && !isReportTypesLoading ? (
          <Stack className={classNames.root}>
            <div className={classNames.titleHeader}>
              <h2>{formatMessage(OverviewPageMessages.title)}</h2>
              <CustomCalender
                onSelectedDatesChanged={onSelectedDatesChanged}
                initialDateRange={dateRange}
                initialRangeType={initialDateRangeType}
                useUtcDatesOnly={true}
              />
            </div>
            <OverviewDataDelayMessageBar
              reportTypes={reportTypesData?.map((r) => r.id) ?? []}
              startDate={dateRange.startDate.toISOString().split('T')[0]}
              endDate={dateRange.endDate.toISOString().split('T')[0]}
            />
            <PubcenterOverviewReportCards dateRange={dateRange} reports={overviewReports} {...props} />
          </Stack>
        ) : (
          <Spinner style={{ top: '50%', left: '50%', position: 'absolute' }} label="Loading..." />
        )}
      </FluentProvider>
    </EmptyLayout>
  );
});
